import { billingPeriod } from 'utilities/billing_period';
import { formatPrice } from 'utilities/money';

import { OfferProps, PlanProps } from '@marketing/types';

// A recurring plan's price pays for one billing period, so say which
export const periodSuffix = (plan: PlanProps) => (
  plan.recurring ? ` / ${billingPeriod(plan)}` : ''
);

// The offer's plan a student can start on for the least, which a "starts at" price quotes
export const startingPlan = (offer: OfferProps) => (
  offer.plans.reduce((cheapest, plan) => (plan.priceInCents < cheapest.priceInCents ? plan : cheapest))
);

interface FormatOfferPriceOptions {
  // Whether to say how often a recurring plan bills, e.g. the ' / month' of '$30 / month'
  showBillingPeriod?: boolean;
}

const formatPlanPrice = (plan: PlanProps, { showBillingPeriod = true }: FormatOfferPriceOptions) => (
  `${formatPrice(plan.fullPriceInCents, 'usd', true)}${showBillingPeriod ? periodSuffix(plan) : ''}`
);

// An offer's price the way the marketing pages quote it, e.g. '$199' or '$30 / month', from its
// cheapest plan. Quotes the price without the sale.
export const formatOfferPrice = (offer: OfferProps, options: FormatOfferPriceOptions = {}) => (
  formatPlanPrice(startingPlan(offer), options)
);

// The offer's plan that renews every given period, e.g. 'month' or 'year'
const planBilledEvery = (offer: OfferProps, period: string) => (
  offer.plans.find((p) => p.recurring && billingPeriod(p) === period)
);

// The same, from the offer's plan billed yearly, e.g. '$129 / year'. Falls back to the cheapest
// plan for an offer with no yearly plan.
export const formatAnnualOfferPrice = (offer: OfferProps, options: FormatOfferPriceOptions = {}) => {
  const plan = planBilledEvery(offer, 'year');

  return plan ? formatPlanPrice(plan, options) : formatOfferPrice(offer, options);
};

// Both of the offer's recurring prices, e.g. ['$30 / month', '$129 / year']. Falls back to the
// cheapest plan alone for an offer without both a monthly and a yearly plan.
export const formatMonthlyAndAnnualOfferPrices = (offer: OfferProps) => {
  const monthlyPlan = planBilledEvery(offer, 'month');
  const annualPlan = planBilledEvery(offer, 'year');

  if (!monthlyPlan || !annualPlan) return [formatOfferPrice(offer)];

  return [formatPlanPrice(monthlyPlan, {}), formatPlanPrice(annualPlan, {})];
};
